import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SafeIcon from '../../common/SafeIcon';
import SEO from '../../common/SEO';
import GoogleMaps from '../../components/GoogleMaps';
import ServicesNavigationFooter from '../../components/ServicesNavigationFooter';
import * as FiIcons from 'react-icons/fi';

const { FiShield, FiThermometer, FiHome, FiCheckCircle, FiArrowRight, FiTrendingDown, FiTool, FiAlertTriangle } = FiIcons;

function Daemmtechnik() {
  const services = [
    {
      title: 'Wärmedämmung Innenbereich',
      description: 'Effiziente Innendämmung für optimale Energieeinsparung und Wohnkomfort.',
      features: ['Reduzierte Heizkosten', 'Schimmelprävention', 'Verbesserte Raumtemperatur']
    },
    {
      title: 'Schalldämmung',
      description: 'Professionelle Schalldämmung für ruhige und entspannte Wohnräume.',
      features: ['Lärmschutz', 'Bessere Akustik', 'Nachbarschaftsfreundlich']
    },
    {
      title: 'Dampfbremsen und Dampfsperren',
      description: 'Schutz vor Feuchtigkeit und Kondensation in der Bausubstanz.',
      features: ['Feuchtigkeitsschutz', 'Bausubstanzschutz', 'Langlebige Lösungen']
    },
    {
      title: 'Energieberatung und -optimierung',
      description: 'Umfassende Beratung für maximale Energieeffizienz Ihres Gebäudes.',
      features: ['Energie-Analyse', 'Förderberatung', 'Kosteneinsparung']
    }
  ];

  const materials = [
    { name: 'Mineralwolle', description: 'Klassischer Dämmstoff mit hervorragenden Eigenschaften' },
    { name: 'Polystyrol (EPS/XPS)', description: 'Leichte und effiziente Dämmplatten' },
    { name: 'Polyurethan (PUR/PIR)', description: 'Hochleistungsdämmstoff mit geringer Aufbauhöhe' },
    { name: 'Naturdämmstoffe', description: 'Ökologische Alternativen wie Hanf, Holzfaser' }
  ];

  const benefits = [
    { icon: FiTrendingDown, title: 'Bis zu 30% Energieeinsparung', description: 'Deutliche Reduzierung der Heizkosten' },
    { icon: FiThermometer, title: 'Optimales Raumklima', description: 'Gleichmäßige Temperaturverteilung' },
    { icon: FiShield, title: 'Schutz vor Feuchtigkeit', description: 'Vermeidung von Schimmelbildung' },
    { icon: FiHome, title: 'Wertsteigerung', description: 'Erhöhung des Immobilienwerts' }
  ];

  return (
    <div className="py-20">
      <SEO 
        title="Dämmtechnik Heinsberg: Wärme- & Schalldämmung vom Profi" 
        description="Sparen Sie Heizkosten! Professionelle Dämmung für Dach, Wand & Decke in Heinsberg. Energieeffizient & nachhaltig. Jetzt beraten lassen!" 
        keywords="Dämmung Heinsberg, Wärmedämmung, Schalldämmung, Dachdämmung, Innendämmung, Energie sparen, Dampfbremse"
        canonical="/daemmtechnik"
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <motion.nav 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="flex items-center space-x-2 text-sm text-gray-600 mb-8"
        >
          <Link to="/" className="hover:text-mk-teal-600">Startseite</Link>
          <span>/</span>
          <Link to="/services" className="hover:text-mk-teal-600">Dienstleistungen</Link>
          <span>/</span>
          <span className="text-gray-900">Dämmtechnik</span>
        </motion.nav>

        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="text-center mb-16"
        >
          <div className="flex items-center justify-center space-x-4 mb-6">
            <div className="w-16 h-16 bg-mk-teal-500 rounded-xl flex items-center justify-center">
              <SafeIcon icon={FiShield} className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900">
              Dämmtechnik
            </h1>
          </div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Professionelle Dämmarbeiten für optimale Energieeffizienz und Wohnkomfort. Sparen Sie Energiekosten und schaffen Sie ein angenehmes Raumklima.
          </p>
        </motion.div>

        {/* Hero Image */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mb-16"
        >
          <img 
            src="https://mx.ausbau.nrw/img/Daemmtechnik_xl.jpg" 
            alt="Dämmtechnik" 
            className="w-full h-96 object-cover rounded-2xl shadow-2xl"
          />
        </motion.div>

        {/* Benefits Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mb-20"
        >
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Vorteile professioneller Dämmung
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div 
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 + index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-mk-teal-500 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={benefit.icon} className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {benefit.title}
                </h3>
                <p className="text-gray-600">
                  {benefit.description}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Services Grid */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="mb-20"
        >
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Unsere Dämmleistungen
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <motion.div 
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 + index * 0.1 }}
                className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow"
              >
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  {service.title}
                </h3>
                <p className="text-gray-600 mb-6">
                  {service.description}
                </p>
                <ul className="space-y-2">
                  {service.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center space-x-3">
                      <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-mk-teal-500" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Materials Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.7 }}
          className="bg-gray-50 p-8 rounded-xl mb-20"
        >
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Hochwertige Dämmmaterialien
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {materials.map((material, index) => (
              <motion.div 
                key={material.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.8 + index * 0.1 }}
                className="bg-white p-6 rounded-lg shadow-md"
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  {material.name}
                </h3>
                <p className="text-gray-600 text-sm">
                  {material.description}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Process Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.9 }}
          className="mb-20"
        >
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Unser Vorgehen
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { step: '1', title: 'Analyse', description: 'Thermische Analyse und Bestandsaufnahme' },
              { step: '2', title: 'Materialwahl', description: 'Auswahl der optimalen Dämmstoffe' },
              { step: '3', title: 'Fachgerechte Montage', description: 'Professionelle Installation durch Experten' }
            ].map((item, index) => (
              <motion.div 
                key={item.step}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 1.0 + index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-mk-red-700 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {item.title}
                </h3>
                <p className="text-gray-600">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* CTA Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1.1 }}
          className="text-center bg-gradient-to-r from-mk-teal-500 to-mk-teal-600 text-white p-12 rounded-xl"
        >
          <h2 className="text-3xl font-bold mb-4">
            Starten Sie Ihr Dämmvorhaben!
          </h2>
          <p className="text-xl text-mk-teal-100 mb-8 max-w-2xl mx-auto">
            Profitieren Sie von geringeren Energiekosten und verbessertem Wohnkomfort. Lassen Sie sich kostenlos beraten!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/contact"
              className="inline-flex items-center justify-center space-x-2 bg-white text-mk-teal-600 px-8 py-3 rounded-sm font-semibold hover:bg-gray-100 transition-colors"
            >
              <span>Kostenlose Beratung</span>
              <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
            </Link>
          </div>
        </motion.div>
      </div>
      
      <ServicesNavigationFooter />
      <GoogleMaps />
    </div>
  );
}

export default Daemmtechnik;